// Lightweight per-page SEO hook. Sets <title>, meta description, canonical, robots.
// Restores previous values on unmount so navigating back keeps things clean.
import { useEffect } from "react";

const SITE = "https://brandandbehavior.com";
const DEFAULT_DESCRIPTION =
  "Brand and Behavior is a Barcelona-based brand advisory practice. Productized brand development, strategy, and ongoing direction — async by default.";

function setMeta(name, content, attr = "name") {
  if (!content) return null;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  const created = !el;
  if (created) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  const prev = el.getAttribute("content");
  el.setAttribute("content", content);
  return { el, prev, created };
}

function setLink(rel, href) {
  if (!href) return null;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  const created = !el;
  if (created) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  const prev = el.getAttribute("href");
  el.setAttribute("href", href);
  return { el, prev, created };
}

export function useSeo({ title, description, path, noindex = false, ogImage } = {}) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const desc = description || DEFAULT_DESCRIPTION;
    const canonical = path && !noindex ? `${SITE}${path}` : null;
    const robots = noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large";

    // When noindex, actively remove any inherited canonical link
    let removedCanonical = null;
    if (noindex) {
      const link = document.head.querySelector('link[rel="canonical"]');
      if (link) {
        removedCanonical = { el: link, href: link.getAttribute("href"), parent: link.parentNode };
        link.parentNode.removeChild(link);
      }
    }

    const restores = [
      setMeta("description", desc),
      setMeta("robots", robots),
      setMeta("og:title", title || "Brand and Behavior", "property"),
      setMeta("og:description", desc, "property"),
      setMeta("og:type", noindex ? "website" : "website", "property"),
      canonical && setMeta("og:url", canonical, "property"),
      ogImage && setMeta("og:image", ogImage, "property"),
      setMeta("twitter:card", "summary_large_image"),
      setMeta("twitter:title", title || "Brand and Behavior"),
      setMeta("twitter:description", desc),
      canonical && setLink("canonical", canonical),
    ].filter(Boolean);

    return () => {
      document.title = prevTitle;
      if (removedCanonical) {
        removedCanonical.parent.appendChild(removedCanonical.el);
      }
      restores.forEach((r) => {
        if (!r) return;
        if (r.created) {
          r.el.parentNode && r.el.parentNode.removeChild(r.el);
        } else if (r.prev !== null && r.prev !== undefined) {
          // meta uses "content", link uses "href" — normalize
          if (r.el.tagName === "LINK") r.el.setAttribute("href", r.prev);
          else r.el.setAttribute("content", r.prev);
        }
      });
    };
  }, [title, description, path, noindex, ogImage]);
}
